import EventFormAssetsRender from 'app/event/eventForm/EventFormAssetsRender';
import EventFormSelectAllAssetsType from 'app/event/eventForm/EventFormSelectAllAssetsType';
import EventFormSelectProducts from 'app/event/eventForm/EventFormSelectProducts';
import SettingsKey from 'app/profile/settings/utils/settingsTypes';
import ActionButton from 'common/components/ActionButton';
import AssetType from 'common/enums/assetTypes';
import EmptyTypes from 'common/enums/emptyTypes';
import useFlexBoxStyles from 'common/flexBox';
import { useSettingValue } from 'common/hooks/useSettingValue';
import { getProductDiscount } from 'common/utils/productDiscount';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { deleteAssetById, setAssets } from 'redux/slices/assetSlice';
import { getExpandAccordionEdited, setExpandAccordionEdited } from 'redux/slices/eventSlice';
import { getOrganization, getProvider } from 'redux/slices/organizationSlice';
import { fetchProductsDiscountsAction, getProductDiscounts } from 'redux/slices/productDiscountSlice';

const ShortVideoFormAssets = (props) => {
    const { open, assets, close, setStep, cancel, step } = props;

    const { t } = useTranslation();
    const { classes: flexBox, cx } = useFlexBoxStyles();

    const dispatch = useDispatch();

    const organization = useSelector(getOrganization);
    const provider = useSelector(getProvider);
    const instantSearch = useSettingValue(SettingsKey.INSTANT_SEARCH);
    const instantSearchEnabled = Boolean(
        organization.provider !== 'shopify-private' && instantSearch && provider?.searchEngineId,
    );

    const searchMinLength = organization.provider === 'shopify-private' ? 1 : 3;

    const expand = useSelector(getExpandAccordionEdited);

    const productDiscounts = useSelector(getProductDiscounts);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        setProducts(
            assets
                ?.filter((asset) => asset.type === AssetType.PRODUCT)
                .map((asset) => ({ ...asset, ...getProductDiscount(asset, productDiscounts) })),
        );
    }, [assets, productDiscounts]);

    useEffect(() => {
        dispatch(fetchProductsDiscountsAction({ queryParams: {} }));
    }, [dispatch]);

    const updateProducts = (products) => {
        dispatch(setExpandAccordionEdited(AssetType.PRODUCT));
        dispatch(
            setAssets([
                ...assets.filter(
                    (asset) =>
                        asset.type !== AssetType.PRODUCT || (asset.type === AssetType.PRODUCT && asset.price === 0),
                ),
                ...products,
            ]),
        );
    };

    const updateAssets = (newAssets) => {
        dispatch(setExpandAccordionEdited('assets'));

        dispatch(
            setAssets([
                ...assets.filter(
                    (asset) => asset.type !== AssetType.CALL_TO_ACTION && asset.type !== AssetType.STICKER,
                ),
                ...newAssets,
            ]),
        );
    };

    const selectProducts = () => {
        open(
            <EventFormSelectProducts
                type={AssetType.PRODUCT}
                searchMinLength={searchMinLength}
                close={close}
                selectedProducts={assets.filter((asset) => asset.type === AssetType.PRODUCT && asset.price > 0)}
                onConfirm={updateProducts}
            />,
        );
    };

    const selectAssets = () => {
        const typesSelect = [
            {
                value: AssetType.CALL_TO_ACTION,
                name: t('eventFormSelectAssets.callToActions'),
            },
            {
                value: AssetType.STICKER,
                name: t('eventFormSelectAssets.stickers'),
            },
        ];
        open(
            <EventFormSelectAllAssetsType
                close={close}
                typesSelect={typesSelect}
                selectedAssets={assets.filter(
                    (asset) => asset.type === AssetType.CALL_TO_ACTION || asset.type === AssetType.STICKER,
                )}
                onConfirm={updateAssets}
            />,
        );
    };

    const deleteProduct = (asset) => {
        dispatch(
            deleteAssetById({
                value: asset.variantGroupId || asset.id,
                idProperty: instantSearchEnabled ? 'variantGroupId' : 'id',
            }),
        );
    };

    const deleteAsset = (asset) => {
        dispatch(deleteAssetById({ value: asset.id }));
    };

    return (
        <div className={cx(flexBox.flex, flexBox.gap2, flexBox.column, flexBox.overflowHidden)}>
            <div
                className={cx(
                    flexBox.flex,
                    flexBox.gap2,
                    flexBox.column,
                    flexBox.overflowXHidden,
                    flexBox.scrollbarNone,
                )}
            >
                <EventFormAssetsRender
                    expanded={expand === 'assets'}
                    emptyType={EmptyTypes.EMPTY_ASSETS}
                    emptyDescription={t('empty.assets')}
                    sectionTitle={t('eventForm.assets')}
                    assets={assets?.filter(
                        (asset) => asset.type === AssetType.CALL_TO_ACTION || asset.type === AssetType.STICKER,
                    )}
                    deleteAsset={deleteAsset}
                    selectAsset={selectAssets}
                    addButtonText={t('eventFormAssets.addAssets')}
                />

                <EventFormAssetsRender
                    emptyType={EmptyTypes.EMPTY_PRODUCTS}
                    emptyDescription={t('empty.products')}
                    expanded={expand === AssetType.PRODUCT}
                    sectionTitle={t('event.eventFormAssets.products')}
                    assets={products}
                    deleteAsset={deleteProduct}
                    selectAsset={selectProducts}
                    addButtonText={t('event.eventFormAssets.addProduct')}
                />
            </div>
            <div className={cx(flexBox.flex, flexBox.gap1, flexBox.justifyContentEnd, flexBox.paddingTop2)}>
                <ActionButton onClick={cancel} color="default" fitWidth>
                    {t('multimedia.multimediaForm.cancel')}
                </ActionButton>

                <ActionButton color="primary" onClick={() => setStep(step + 1)} autoFocus fitWidth>
                    {t('game.gameActions.save')}
                </ActionButton>
            </div>
        </div>
    );
};

export default ShortVideoFormAssets;
